<template>
  <section id="brands" class="py-24 bg-gray-900" ref="brandsSection">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-16" ref="brandsTitle">
        <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('brands.label') }}</span>
        <h2 class="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
          {{ $t('brands.title') }}
        </h2>
        <p class="text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed text-justify">
          {{ $t('brands.description') }}
        </p>
      </div>

      <!-- Brands Slider -->
      <div class="relative">
        <Swiper
          :modules="[Navigation, Pagination, Autoplay]"
          :slides-per-view="2"
          :space-between="30"
          :breakpoints="{
            640: { slidesPerView: 3, spaceBetween: 40 },
            768: { slidesPerView: 4, spaceBetween: 50 },
            1024: { slidesPerView: 5, spaceBetween: 60 },
            1280: { slidesPerView: 6, spaceBetween: 60 }
          }"
          :autoplay="{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          }"
          :loop="true"
          :grabCursor="true"
          class="brands-swiper"
        >
          <SwiperSlide v-for="(brand, index) in brands" :key="index">
            <div 
              class="group brand-card relative bg-gray-800 rounded-2xl p-6 lg:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-700 hover:border-primary/20 flex items-center justify-center h-32 lg:h-40"
              :ref="el => { if (el) brandCards[index] = el }"
            >
              <!-- Brand Logo/Name -->
              <div class="w-full h-full flex items-center justify-center">
                <div class="text-gray-400 text-center">
                  <div class="text-xl lg:text-2xl font-bold mb-2">{{ brand.icon }}</div>
                  <div class="text-sm lg:text-base font-semibold">{{ brand.name }}</div>
                </div>
              </div>
              
              <!-- Hover Effect -->
              <div class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { textReveal, staggerChildren } from '../utils/animations'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'

const { t, locale } = useI18n()
const brandsSection = ref(null)
const brandsTitle = ref(null)
const brandCards = ref([])

// Helper function to get array translations
const getArrayTranslation = (key) => {
  const keys = key.split('.')
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  let value = messages
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key} at ${k}`)
      return []
    }
  }
  return Array.isArray(value) ? value : []
}

// Get brands from translation files dynamically
const brands = computed(() => {
  return getArrayTranslation('brands.items') || []
})

onMounted(() => {
  const brandsObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        textReveal(brandsTitle.value, 0)
        staggerChildren(brandsSection.value, '.brand-card', 100)
      }
    })
  }, { threshold: 0.2 })
  
  if (brandsSection.value) {
    brandsObserver.observe(brandsSection.value)
  }
})
</script>

<style scoped>
/* Swiper Custom Styles */
.brands-swiper {
  --swiper-navigation-size: 0px;
  --swiper-pagination-color: #008067;
  --swiper-pagination-bullet-size: 8px;
  --swiper-pagination-bullet-horizontal-gap: 6px;
}

.brands-swiper .swiper-slide {
  height: auto;
  display: flex;
}

.brand-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.brand-card:hover {
  transform: translateY(-4px);
}

/* Smooth animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.brand-card {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

.brand-card:nth-child(1) { animation-delay: 0.1s; }
.brand-card:nth-child(2) { animation-delay: 0.2s; }
.brand-card:nth-child(3) { animation-delay: 0.3s; }
.brand-card:nth-child(4) { animation-delay: 0.4s; }
.brand-card:nth-child(5) { animation-delay: 0.5s; }
.brand-card:nth-child(6) { animation-delay: 0.6s; }
</style>

